// =====================================================
// PDF VIEWER — Visor de PDF con PDF.js
// Sin controles nativos del navegador, integrado con
// el diseño de la aplicación.
// =====================================================
import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle, ZoomIn, ZoomOut } from 'lucide-react';

interface PdfViewerProps {
  url: string;
}

export function PdfViewer({ url }: PdfViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pdf, setPdf] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const renderTaskRef = useRef<any>(null);

  // Cargar el documento PDF
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setPdf(null);
    setCurrentPage(1);

    const load = async () => {
      try {
        // Importar PDF.js dinámicamente
        const pdfjs = await import('pdfjs-dist');
        // Worker necesario para PDF.js — usar versión local del node_modules
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.js',
          import.meta.url,
        ).toString();

        const doc = await pdfjs.getDocument(url).promise;
        if (cancelled) return;
        setPdf(doc);
        setTotalPages(doc.numPages);
        setIsLoading(false);
      } catch (err: any) {
        if (!cancelled) {
          setError('No se pudo cargar el documento PDF');
          setIsLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [url]);

  // Renderizar la página actual
  useEffect(() => {
    if (!pdf || !canvasRef.current) return;

    const render = async () => {
      try {
        // Cancelar render anterior si existe
        if (renderTaskRef.current) {
          await renderTaskRef.current.cancel().catch(() => {});
          renderTaskRef.current = null;
        }

        const page = await pdf.getPage(currentPage);
        const canvas = canvasRef.current;
        if (!canvas) return;

        const viewport = page.getViewport({ scale });
        const context = canvas.getContext('2d');
        if (!context) return;

        // Soporte para pantallas retina
        const devicePixelRatio = window.devicePixelRatio || 1;
        canvas.width = viewport.width * devicePixelRatio;
        canvas.height = viewport.height * devicePixelRatio;
        canvas.style.width = viewport.width + 'px';
        canvas.style.height = viewport.height + 'px';
        context.scale(devicePixelRatio, devicePixelRatio);

        const task = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = task;
        await task.promise;
        renderTaskRef.current = null;
      } catch (err: any) {
        // Ignorar cancelaciones
        if (err?.name !== 'RenderingCancelledException') {
          setError('Error al renderizar la página');
        }
      }
    };

    render();
  }, [pdf, currentPage, scale]);

  const goTo = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const zoomIn  = () => setScale(s => Math.min(s + 0.2, 3));
  const zoomOut = () => setScale(s => Math.max(s - 0.2, 0.5));

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
        <p className="text-sm text-gray-400">Cargando documento...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* ── Barra de controles ────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/80">
        {/* Navegación de páginas */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => goTo(currentPage - 1)}
            disabled={currentPage <= 1}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-500 tabular-nums min-w-[72px] text-center">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => goTo(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Página siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={zoomOut}
            disabled={scale <= 0.5}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Reducir zoom"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-400 tabular-nums w-10 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={scale >= 3}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Aumentar zoom"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── Canvas ────────────────────────────────────────────────────── */}
      <div className="overflow-auto bg-gray-100 flex justify-center p-4" style={{ maxHeight: '68vh' }}>
        <canvas
          ref={canvasRef}
          className="shadow-md rounded"
        />
      </div>
    </div>
  );
}
